/**
 * API Service for Waterlife Pools & Spas
 * Handles all backend API calls
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Contact Form Submission
 */
export const submitContact = async (formData) => {
  try {
    const response = await fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit contact form');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Contact submission error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Upload Pool Analysis Photos
 */
export const uploadPoolPhotos = async (files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await fetch(`${API_URL}/api/pool-analysis/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload photos');
    }

    const data = await response.json();
    return { success: true, files: data.files };
  } catch (error) {
    console.error('Photo upload error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate Pool Cost Estimate
 */
export const calculatePoolCost = async (poolType, poolSize, features) => {
  try {
    const response = await fetch(`${API_URL}/api/pool-analysis/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pool_type: poolType,
        pool_size: poolSize,
        features: features,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to calculate estimate');
    }

    const data = await response.json();
    return { success: true, estimate: data };
  } catch (error) {
    console.error('Cost calculation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Submit Pool Analysis
 */
export const submitPoolAnalysis = async (analysisData, photoUrls) => {
  try {
    const response = await fetch(`${API_URL}/api/pool-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: analysisData.name,
        email: analysisData.email,
        phone: analysisData.phone,
        pool_type: analysisData.poolType,
        pool_size: analysisData.poolSize,
        features: analysisData.features,
        address: analysisData.address || null,
        timeline: analysisData.timeline || null,
        photo_urls: photoUrls,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit pool analysis');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Pool analysis submission error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Admin: Get All Contacts
 */
export const getContacts = async (limit = 100, status = null) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (status) params.append('status', status);

    const response = await fetch(`${API_URL}/api/contact?${params}`);

    if (!response.ok) {
      throw new Error('Failed to fetch contacts');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Get contacts error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Admin: Get All Pool Analyses
 */
export const getPoolAnalyses = async (limit = 100, status = null) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (status) params.append('status', status);

    const response = await fetch(`${API_URL}/api/pool-analysis?${params}`);

    if (!response.ok) {
      throw new Error('Failed to fetch pool analyses');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Get pool analyses error:', error);
    return { success: false, error: error.message };
  }
};
